// Helper para hacer consultas GetFeatureInfo a servicios WMS oficiales.
//
// GetFeatureInfo devuelve, para un punto concreto (lat, lon), los atributos
// del feature que interseca ese punto en una capa dada. Sirve para saber
// automáticamente si un edificio cae dentro de una ZARI, si hay movimientos
// del terreno cerca (IGME BDMoves con buffer), etc.
//
// Los WMS oficiales españoles suelen aceptar CORS para GetCapabilities y
// GetLegendGraphic, pero GetFeatureInfo puede fallar por CORS en algunos
// servidores. En caso de fallo, la consulta se marca como "unavailable" y el
// informe indica claramente que la verificación no pudo completarse.

// Endpoint del servidor Node.js que hace GetFeatureInfo server-side.
// Ruta pública a través de nginx: https://appbyhaus.com/pdf/wms-featureinfo.
// En desarrollo local: http://localhost:3010/wms-featureinfo.
function wmsFeatureInfoEndpoint() {
  if (typeof window !== 'undefined') {
    const isProd = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    return isProd ? '/pdf/wms-featureinfo' : 'http://localhost:3010/wms-featureinfo'
  }
  return 'http://localhost:3010/wms-featureinfo'
}

// Consulta GetFeatureInfo a un WMS a través del proxy server-side. Devuelve:
//   { ok: bool, features: [], raw?: string, error?: string }
//
// IMPORTANTE: no hacemos el fetch directo al WMS desde el navegador porque los
// servidores oficiales españoles (pcivil.icgc.cat, mapas.igme.es, wms-volcanologia)
// no envían cabeceras CORS y el navegador bloquea la petición cross-origin. El
// servidor Node.js (pdf-server) hace el fetch y devuelve el resultado ya parseado.
export async function wmsGetFeatureInfo({
  url,
  layer,
  lat,
  lon,
  bufferMeters = 200,
  version = '1.3.0',
  axisOrder = 'yx',
  timeoutMs = 15000,
}) {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    const res = await fetch(wmsFeatureInfoEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wmsUrl: url, layer, lat, lon, bufferMeters, version, axisOrder }),
      signal: controller.signal,
    })
    clearTimeout(timer)
    if (!res.ok) return { ok: false, features: [], error: `Proxy HTTP ${res.status}` }
    const data = await res.json()
    if (data.error) return { ok: false, features: [], error: data.error }
    return { ok: true, features: data.features || [], raw: data.raw }
  } catch (e) {
    return { ok: false, features: [], error: e.message || String(e) }
  }
}

// ── Consultas específicas por capa ────────────────────────────────────

export async function checkZARI(lat, lon) {
  // ZARI = Zonas de Alto Riesgo de Incendio (Gobierno de Canarias)
  const r = await wmsGetFeatureInfo({
    url: 'https://idecan2.grafcan.es/ServicioWMS/ZARI',
    layer: 'ZARI',
    lat, lon,
    bufferMeters: 100,
  })
  if (!r.ok) return { checked: false, error: r.error }
  const inZone = r.features.length > 0
  return {
    checked: true,
    in_zari: inZone,
    features_count: r.features.length,
    check_date: new Date().toISOString().slice(0, 10),
  }
}

export async function checkIGMEBDMoves(lat, lon, bufferMeters = 500) {
  // BDMoves = movimientos del terreno catalogados por el IGME
  const r = await wmsGetFeatureInfo({
    url: 'https://mapas.igme.es/gis/services/BasesDatos/IGME_BDMoves_ES/MapServer/WMSServer',
    layer: '0',
    lat, lon,
    bufferMeters,
  })
  if (!r.ok) return { checked: false, error: r.error }
  return {
    checked: true,
    moves_registered: r.features.length > 0,
    features_count: r.features.length,
    check_date: new Date().toISOString().slice(0, 10),
  }
}

export async function checkErupcionesHistoricas(lat, lon, bufferMeters = 5000) {
  // Erupciones históricas (IGN Vulcanología) — solo tiene sentido en Canarias
  const r = await wmsGetFeatureInfo({
    url: 'https://wms-volcanologia.ign.es/volcanologia',
    layer: 'erupciones',
    lat, lon,
    bufferMeters,
  })
  if (!r.ok) return { checked: false, error: r.error }
  return {
    checked: true,
    eruptions_nearby: r.features.length,
    features: r.features.slice(0, 3),  // primeros 3 registros históricos
    check_date: new Date().toISOString().slice(0, 10),
  }
}

// Leyenda oficial del INFOCAT (Generalitat de Catalunya) — colores extraídos
// del WMS de pcivil.icgc.cat/ogc/geoservei layer=infocat_perill. Se ordenan
// de mayor a menor severidad. Cada categoría es el color RGB EXACTO que el
// servidor pinta para esa clase.
const INFOCAT_PALETTE = [
  { rgb: [160,  56,  0], level: 'extrem',   label: 'EXTREM' },
  { rgb: [230,  76,  0], level: 'molt_alt', label: 'MOLT ALT' },
  { rgb: [255, 167,  1], level: 'alt',      label: 'ALT' },
  { rgb: [251, 211, 128], level: 'moderat',  label: 'MODERAT' },
  { rgb: [253, 253, 195], level: 'baix',     label: 'BAIX' },
]

// Distancia euclídea en espacio RGB (aproximación razonable para colores
// vivos como los de la leyenda oficial). Si en el futuro el servidor cambia
// los tonos ligeramente, este matching aún encontrará la categoría correcta.
function rgbDistance(a, b) {
  return Math.sqrt(
    (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2
  )
}

// Recibe un color RGB detectado y devuelve la categoría INFOCAT más cercana.
// tolerance define el umbral máximo de distancia (0-441 aprox). Con tolerancia
// 60 el matching es muy conservador — si el color no coincide con nada, no
// se asigna categoría (evitamos falsos positivos).
export function matchInfocatColor(r, g, b, tolerance = 60) {
  let best = null, bestDist = Infinity
  for (const entry of INFOCAT_PALETTE) {
    const d = rgbDistance([r, g, b], entry.rgb)
    if (d < bestDist) { best = entry; bestDist = d }
  }
  if (best && bestDist <= tolerance) return { ...best, distance: bestDist }
  return null
}

// Endpoint del pdf-server que hace GetMap 1x1 y decodifica el pixel. Ruta
// pública a través de nginx: https://appbyhaus.com/pdf/pixel-color
// En desarrollo local: http://localhost:3010/pixel-color
function pixelColorEndpoint() {
  if (typeof window !== 'undefined') {
    const isProd = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    return isProd ? '/pdf/pixel-color' : 'http://localhost:3010/pixel-color'
  }
  return 'http://localhost:3010/pixel-color'
}

// Consulta al pdf-server el color del pixel para un punto de un WMS. Devuelve
// { r, g, b, hex } o null si falla / el WMS no responde.
export async function fetchWmsPixelColor({ wmsUrl, layer, lat, lon, version = '1.3.0', crs = 'EPSG:4326' }) {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 10000)
    const res = await fetch(pixelColorEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wmsUrl, layer, lat, lon, version, crs }),
      signal: controller.signal,
    })
    clearTimeout(timer)
    if (!res.ok) return null
    const data = await res.json()
    if (data && typeof data.r === 'number' && data.alpha > 0) return data
    return null
  } catch (e) {
    console.warn('[fetchWmsPixelColor] no disponible:', e.message)
    return null
  }
}

export async function checkPerillIncendi(lat, lon) {
  // 1) Intento clásico por GetFeatureInfo (por si algún día publican el atributo)
  const r = await wmsGetFeatureInfo({
    url: 'https://pcivil.icgc.cat/ogc/geoservei?map=/opt/idec/dades/pcivil/risc_incendis.map',
    layer: 'infocat_perill',
    lat, lon,
    bufferMeters: 100,
  })
  if (!r.ok) return { checked: false, error: r.error }

  let level = null
  let levelSource = null
  if (r.features.length > 0) {
    const props = r.features[0].properties || {}
    level = props.perill_nivell || props.PERILL || props.perill || null
    if (level) levelSource = 'wms-featureinfo'
  }

  // 2) Fallback: si estamos en zona pero sin nivel, leer el color del pixel
  //    con GetMap 1x1 y matchear con la leyenda oficial INFOCAT.
  let pixel = null, match = null
  if (r.features.length > 0 && !level) {
    pixel = await fetchWmsPixelColor({
      wmsUrl: 'https://pcivil.icgc.cat/ogc/geoservei?map=/opt/idec/dades/pcivil/risc_incendis.map',
      layer: 'infocat_perill',
      lat, lon,
    })
    if (pixel) {
      match = matchInfocatColor(pixel.r, pixel.g, pixel.b)
      if (match) {
        level = match.level
        levelSource = 'wms-pixel'
      }
    }
  }

  return {
    checked: true,
    level,
    level_label: match ? match.label : (level ? level.toUpperCase() : null),
    level_source: levelSource,
    pixel_rgb: pixel ? { r: pixel.r, g: pixel.g, b: pixel.b, hex: pixel.hex } : null,
    in_zone: r.features.length > 0,
    check_date: new Date().toISOString().slice(0, 10),
    note: (r.features.length > 0 && !level)
      ? 'El punto está dentro de una zona de perill INFOCAT. No se ha podido determinar el nivel automáticamente (WMS no publica el atributo y el servicio de análisis de pixel no respondió). Revise el color en el mapa adjunto para clasificar (baix / moderat / alt / molt alt / extrem).'
      : null,
  }
}

export async function checkPGA475(lat, lon) {
  // PGA 475 años del IGN — mapa 2015 · aceleración pico terreno para
  // el escenario con probabilidad de excedencia del 10 % en 50 años.
  const r = await wmsGetFeatureInfo({
    url: 'https://www.ign.es/wms-inspire/geofisica',
    layer: 'HazardArea2015.PGA475_p',
    lat, lon,
    bufferMeters: 500,
  })
  if (!r.ok) return { checked: false, error: r.error }
  let value = null
  if (r.features.length > 0) {
    const props = r.features[0].properties || {}
    // El WMS devuelve el valor bajo distintos posibles nombres de campo
    value = props.PIXEL_VALUE ?? props.pixel_value ?? props.GRAY_INDEX
      ?? props.value ?? props.VALUE ?? props.pga ?? null
    if (value != null) value = Number(value)
  }
  return {
    checked: true,
    pga_g: (value != null && isFinite(value)) ? value : null,
    check_date: new Date().toISOString().slice(0, 10),
  }
}

export async function checkEMS98(lat, lon) {
  // Intensidad EMS-98 (mapa 2015) — probabilidad de excedencia 475 años.
  const r = await wmsGetFeatureInfo({
    url: 'https://www.ign.es/wms-inspire/geofisica',
    layer: 'HazardArea2015.Int475',
    lat, lon,
    bufferMeters: 500,
  })
  if (!r.ok) return { checked: false, error: r.error }
  let value = null
  if (r.features.length > 0) {
    const props = r.features[0].properties || {}
    value = props.PIXEL_VALUE ?? props.pixel_value ?? props.GRAY_INDEX
      ?? props.value ?? props.VALUE ?? props.intensity ?? null
    if (value != null) value = Number(value)
  }
  return {
    checked: true,
    ems98: (value != null && isFinite(value)) ? value : null,
    check_date: new Date().toISOString().slice(0, 10),
  }
}

export async function checkRiscGreu(lat, lon) {
  // Zonas de risc greu d'incendi (Generalitat) — indicativo Sí/No dentro de zona
  const r = await wmsGetFeatureInfo({
    url: 'https://pcivil.icgc.cat/ogc/geoservei?map=/opt/idec/dades/pcivil/risc_incendis.map',
    layer: 'zones_risc_greu_incendis',
    lat, lon,
    bufferMeters: 100,
  })
  if (!r.ok) return { checked: false, error: r.error }
  return {
    checked: true,
    in_risc_greu: r.features.length > 0,   // el WMS devuelve feature aunque sin atributos ⇒ zona confirmada
    check_date: new Date().toISOString().slice(0, 10),
  }
}
